import httpService from "./http.service";
import { getAuthApiRoute } from "../utils/api.utils";
import {
  clearAuthSession,
  setAuthenticatedSession,
  setTemporaryAuthTokens,
} from "./authStorage";

export { refreshAuthSession } from "./http.service";

function unwrapResponse(response) {
  if (!response) return null;
  var body = response.data;
  return body && Object.prototype.hasOwnProperty.call(body, "data")
    ? body.data
    : body;
}

function toFormData(values) {
  var formData = new FormData();

  Object.keys(values || {}).forEach(function (key) {
    var value = values[key];
    if (value !== undefined && value !== null) {
      var isBlob = typeof Blob !== "undefined" && value instanceof Blob;
      formData.append(key, isBlob ? value : String(value));
    }
  });

  return formData;
}

function multipartConfig() {
  return { headers: { "Content-Type": "multipart/form-data" } };
}

export function getAuthErrorMessage(error, fallback) {
  var message =
    error &&
    error.response &&
    error.response.data &&
    error.response.data.message;

  if (Array.isArray(message)) {
    return message[0] || fallback || "خطایی رخ داده است";
  }

  return (
    message ||
    (error && error.message) ||
    fallback ||
    "خطایی رخ داده است"
  );
}

export async function generateLoginToken(userAgent) {
  var response = await httpService.post(
    getAuthApiRoute("generate-login-token"),
    {
      userAgent:
        userAgent ||
        (typeof navigator !== "undefined" ? navigator.userAgent : "WebTV"),
    },
    { skipAuth: true, skipAuthRefresh: true }
  );
  return unwrapResponse(response);
}

export async function sendLoginOTP(params) {
  var response = await httpService.post(
    getAuthApiRoute("send-login-otp"),
    params,
    { skipAuth: true, skipAuthRefresh: true }
  );
  return unwrapResponse(response);
}

export async function loginRegister(params) {
  var response = await httpService.post(
    getAuthApiRoute("login-register"),
    params,
    { skipAuth: true, skipAuthRefresh: true }
  );
  var tokens = unwrapResponse(response);
  setTemporaryAuthTokens(tokens);
  return tokens;
}

export async function selectProfile(params) {
  var response = await httpService.post(
    getAuthApiRoute("select-profile"),
    params
  );
  var session = unwrapResponse(response);
  setAuthenticatedSession(session, session && session.profile);
  return session;
}

export async function selectQRProfile(profileId, loginAttemptToken, temporaryToken) {
  var response = await httpService.post(
    getAuthApiRoute("qr/select-profile"),
    {
      profileId: profileId,
      loginAttemptToken: loginAttemptToken || undefined,
    },
    {
      headers: { Authorization: "Bearer " + temporaryToken },
      skipAuthRefresh: true,
    }
  );
  var session = unwrapResponse(response);
  setAuthenticatedSession(session, session && session.profile);
  return session;
}

export async function switchProfile(profileId) {
  var response = await httpService.post(getAuthApiRoute("switch-profile"), {
    profileId: profileId,
  });
  var session = unwrapResponse(response);
  setAuthenticatedSession(session, session && session.profile);
  return session;
}

export async function getUser() {
  var response = await httpService.get(getAuthApiRoute("user"));
  return unwrapResponse(response);
}

export async function updateUser(updates) {
  var response = await httpService.patch(
    getAuthApiRoute("user"),
    toFormData(updates),
    multipartConfig()
  );
  return unwrapResponse(response);
}

export async function addProfile(profile) {
  var response = await httpService.post(
    getAuthApiRoute("profile"),
    toFormData(profile),
    multipartConfig()
  );
  return unwrapResponse(response);
}

export async function updateProfile(profile) {
  var response = await httpService.patch(
    getAuthApiRoute("profile"),
    toFormData(profile),
    multipartConfig()
  );
  return unwrapResponse(response);
}

export async function deleteProfile(profileId, otpCredentials) {
  var config = {};
  if (otpCredentials) {
    config.headers = {
      "x-mobile-number": String(otpCredentials.mobileNumber),
      "x-otp": String(otpCredentials.otp),
    };
    config.skipAuth = true;
    config.skipAuthRefresh = true;
  }

  var response = await httpService.delete(
    getAuthApiRoute("profile/" + encodeURIComponent(profileId)),
    config
  );
  return unwrapResponse(response);
}

export async function getUserWithToken(token) {
  var response = await httpService.get(getAuthApiRoute("user"), {
    headers: { Authorization: "Bearer " + token },
    skipAuthRefresh: true,
  });
  return unwrapResponse(response);
}

export async function getUserWithOTP(mobileNumber, otp) {
  var response = await httpService.get(getAuthApiRoute("user"), {
    headers: {
      "x-mobile-number": String(mobileNumber),
      "x-otp": String(otp),
    },
    skipAuth: true,
    skipAuthRefresh: true,
  });
  return unwrapResponse(response);
}

export async function getSessions() {
  var response = await httpService.get(getAuthApiRoute("sessions"));
  var data = unwrapResponse(response);
  return (data && data.sessions) || data || [];
}

export async function getSessionsWithToken(token) {
  var response = await httpService.get(getAuthApiRoute("sessions"), {
    headers: { Authorization: "Bearer " + token },
    skipAuthRefresh: true,
  });
  var data = unwrapResponse(response);
  return (data && data.sessions) || data || [];
}

export async function deleteSession(sessionId) {
  var response = await httpService.delete(getAuthApiRoute("session"), {
    data: { sessionId: sessionId },
  });
  return unwrapResponse(response);
}

export async function deleteSessionWithToken(token, sessionId) {
  var response = await httpService.delete(getAuthApiRoute("session"), {
    headers: { Authorization: "Bearer " + token },
    data: { sessionId: sessionId },
    skipAuthRefresh: true,
  });
  return unwrapResponse(response);
}

export async function logout() {
  try {
    var response = await httpService.post(getAuthApiRoute("logout"));
    return unwrapResponse(response);
  } finally {
    clearAuthSession();
  }
}

export async function generateQRCode(device) {
  var response = await httpService.post(
    getAuthApiRoute("qr/generate"),
    { device: device || "WebTV Device" },
    { skipAuth: true, skipAuthRefresh: true }
  );
  return unwrapResponse(response);
}

export async function getQRStatus(qrId, sessionId) {
  var response = await httpService.post(
    getAuthApiRoute("qr/status"),
    { qrId: qrId, sessionId: sessionId },
    { skipAuth: true, skipAuthRefresh: true }
  );
  return unwrapResponse(response);
}

export async function scanQRCode(params) {
  var response = await httpService.post(
    getAuthApiRoute("qr/scan"),
    params,
    { skipAuth: true, skipAuthRefresh: true }
  );
  return unwrapResponse(response);
}

export async function confirmQRCode(params) {
  var response = await httpService.post(
    getAuthApiRoute("qr/confirm"),
    params,
    { skipAuth: true, skipAuthRefresh: true }
  );
  return unwrapResponse(response);
}

export async function cancelQRCode(qrId, sessionId) {
  var response = await httpService.post(
    getAuthApiRoute("qr/cancel"),
    { qrId: qrId, sessionId: sessionId },
    { skipAuth: true, skipAuthRefresh: true }
  );
  return unwrapResponse(response);
}

export async function deactivateAccount() {
  var response = await httpService.patch(getAuthApiRoute("user/deactive"));
  return unwrapResponse(response);
}

export async function activateAccount(mobileNumber, otp) {
  var response = await httpService.patch(
    getAuthApiRoute("user/active"),
    { mobileNumber: mobileNumber, otp: otp },
    { skipAuth: true, skipAuthRefresh: true }
  );
  return unwrapResponse(response);
}
